import React from 'react';
import { useBooking } from '../../contexts/booking/BookingContext';
import ServiceSelection from './ServiceSelection';
import StaffSelection from './StaffSelection';
import DateTimeSelection from './DateTimeSelection';
import CustomerInfoForm from './CustomerInfoForm';
import BookingSummary from './BookingSummary';
import PaymentForm from './PaymentForm';
import BookingConfirmation from './BookingConfirmation';

// Steps in our booking wizard
const STEPS = [
  'Select Services',
  'Choose Staff',
  'Select Date & Time',
  'Your Information',
  'Review & Confirm',
  'Payment'
];

const BookingWizard: React.FC = () => {
  const { bookingState, prevStep } = useBooking();
  const { currentStep } = bookingState;

  // Scroll to top when step changes
  React.useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth'
    });
  }, [currentStep]);

  // Render the current step component
  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <ServiceSelection />;
      case 2:
        return <StaffSelection />;
      case 3:
        return <DateTimeSelection />;
      case 4:
        return <CustomerInfoForm />;
      case 5:
        return <BookingSummary />;
      case 6:
        return <PaymentForm />;
      case 7:
        return <BookingConfirmation />;
      default:
        return <ServiceSelection />;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F8F6F2] via-[#E8F5F2] to-[#F9E9E9] font-sans">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <h1 className="text-2xl text-[#232347] font-serif">Book Your Appointment</h1>
        </div>
      </div>

      {/* Progress Steps - Hide on confirmation step */}
      {currentStep <= 5 && (
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            {STEPS.map((step, index) => (
              <div key={index} className="flex flex-col items-center">
                <div 
                  className={`w-8 h-8 rounded-full flex items-center justify-center mb-2 
                    ${index + 1 === currentStep 
                      ? 'bg-[#B2D8C5] text-white' 
                      : index + 1 < currentStep 
                        ? 'bg-[#232347] text-white' 
                        : 'bg-gray-200 text-gray-500'}`}
                >
                  {index + 1 < currentStep ? (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  ) : (
                    index + 1
                  )}
                </div>
                <span 
                  className={`text-xs hidden md:block ${
                    index + 1 === currentStep 
                      ? 'text-[#232347] font-medium' 
                      : 'text-gray-500'
                  }`}
                >
                  {step}
                </span>
              </div>
            ))}
          </div>
          <div className="flex w-full mt-2">
            <div className="hidden md:flex w-full">
              {STEPS.map((_, index) => (
                <div 
                  key={index} 
                  className={`h-1 ${
                    index === STEPS.length - 1 
                      ? '' 
                      : index + 1 < currentStep 
                        ? 'bg-[#232347]' 
                        : index + 1 === currentStep 
                          ? 'bg-[#B2D8C5]' 
                          : 'bg-gray-200'
                  } ${index === STEPS.length - 1 ? '' : 'flex-1'}`}
                />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Step Content */}
      <div className="container mx-auto px-4 py-6">
        {/* Back button - Hide on first step and confirmation */}
        {currentStep > 1 && currentStep <= 5 && (
          <button 
            onClick={prevStep}
            className="flex items-center text-[#232347] mb-4 hover:text-[#B2D8C5] transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M9.707 14.707a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 1.414L7.414 9H15a1 1 0 110 2H7.414l2.293 2.293a1 1 0 010 1.414z" clipRule="evenodd" />
            </svg>
            Back
          </button>
        )}
        
        {/* Current step */}
        <div className="bg-white/80 backdrop-blur-sm rounded-lg shadow-md p-6">
          {renderStep()}
        </div>
      </div>
    </div>
  );
};

export default BookingWizard;
